(() => {
  const ROOT_SELECTOR = '#MainContent';
  const MIN_WIDTH = 220;
  const MIN_HEIGHT = 58;
  const EXCLUDED_ANCESTORS = [
    '.product__media-wrapper',
    '.product__media-list',
    '.thumbnail-slider',
    '.cart-drawer',
    '.drawer',
    '.predictive-search',
    '.modal',
    '[role="dialog"]',
    '.mf-banner-slider',
    '.mf-header',
    '.mf-header-quicknav',
    '.shopify-payment-button'
  ].join(',');

  const EXISTING_CONTROLS = [
    '.slider-buttons',
    'button[class*="arrow"]',
    'button[class*="prev"]',
    'button[class*="next"]',
    '[data-prev]',
    '[data-next]',
    '[data-mk-prev]',
    '[data-mk-next]',
    '[data-ks-prev]',
    '[data-ks-next]'
  ].join(',');

  const makeButton = (direction) => {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = `mf-auto-scroll-arrow mf-auto-scroll-arrow--${direction}`;
    button.setAttribute('aria-label', direction === 'prev' ? 'Ver itens anteriores' : 'Ver mais itens para o lado');
    button.innerHTML = direction === 'prev'
      ? '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="m15 5-7 7 7 7" stroke="currentColor" stroke-width="2.3" stroke-linecap="round" stroke-linejoin="round"/></svg>'
      : '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="m9 5 7 7-7 7" stroke="currentColor" stroke-width="2.3" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    return button;
  };

  const isEligible = (element) => {
    if (!(element instanceof HTMLElement)) return false;
    if (element.dataset.mfAutoScrollReady === 'true') return false;
    if (element.closest(EXCLUDED_ANCESTORS)) return false;
    if (element.clientWidth < MIN_WIDTH || element.clientHeight < MIN_HEIGHT) return false;

    const style = window.getComputedStyle(element);
    const overflowX = style.overflowX;
    const scrollSnap = style.scrollSnapType || '';
    const canScroll = overflowX === 'auto' || overflowX === 'scroll' || scrollSnap.startsWith('x');

    if (!canScroll) return false;
    if (element.scrollWidth <= element.clientWidth + 6) return false;

    const parent = element.parentElement;
    if (!parent) return false;
    if (parent.querySelector(':scope > .mf-auto-scroll-arrow')) return false;

    const nearbyControlHost = element.closest('.carousel, .slider-mobile-gutter, slider-component, [data-slider], [data-carousel]') || parent;
    if (nearbyControlHost.querySelector(EXISTING_CONTROLS)) return false;

    return true;
  };

  const getScrollStep = (element) => {
    const children = Array.from(element.children).filter((child) => child instanceof HTMLElement);
    const firstVisible = children.find((child) => child.getBoundingClientRect().width > 40);
    if (!firstVisible) return Math.max(element.clientWidth * 0.78, 240);

    const style = window.getComputedStyle(element);
    const gap = Number.parseFloat(style.columnGap || style.gap || '0') || 0;
    return Math.max(firstVisible.getBoundingClientRect().width + gap, 180);
  };

  const enhance = (scroller) => {
    if (!isEligible(scroller)) return;

    const host = scroller.parentElement;
    const previous = makeButton('prev');
    const next = makeButton('next');

    host.classList.add('mf-auto-scroll-host');
    host.append(previous, next);
    scroller.dataset.mfAutoScrollReady = 'true';

    const positionButtons = () => {
      const hostRect = host.getBoundingClientRect();
      const scrollerRect = scroller.getBoundingClientRect();
      const center = scrollerRect.top - hostRect.top + (scrollerRect.height / 2);
      host.style.setProperty('--mf-auto-scroll-y', `${Math.max(20, center)}px`);
    };

    const update = () => {
      const maxScroll = Math.max(0, scroller.scrollWidth - scroller.clientWidth);
      const hasOverflow = maxScroll > 6;
      previous.hidden = !hasOverflow;
      next.hidden = !hasOverflow;
      previous.disabled = !hasOverflow || scroller.scrollLeft <= 5;
      next.disabled = !hasOverflow || scroller.scrollLeft >= maxScroll - 5;
      positionButtons();
    };

    previous.addEventListener('click', () => {
      scroller.scrollBy({ left: -getScrollStep(scroller), behavior: 'smooth' });
    });

    next.addEventListener('click', () => {
      scroller.scrollBy({ left: getScrollStep(scroller), behavior: 'smooth' });
    });

    scroller.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update, { passive: true });

    update();
    window.setTimeout(update, 250);
  };

  const scan = (scope = document) => {
    const root = scope.matches?.(ROOT_SELECTOR) ? scope : scope.querySelector?.(ROOT_SELECTOR) || document.querySelector(ROOT_SELECTOR);
    if (!root) return;

    const candidates = root.querySelectorAll('*');
    candidates.forEach(enhance);
  };

  const scheduleScan = (() => {
    let frame = null;
    return (scope = document) => {
      if (frame !== null) window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => {
        frame = null;
        scan(scope);
      });
    };
  })();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => scheduleScan(document), { once: true });
  } else {
    scheduleScan(document);
  }

  document.addEventListener('shopify:section:load', (event) => scheduleScan(event.target));
  document.addEventListener('shopify:section:reorder', () => scheduleScan(document));

  const main = document.querySelector(ROOT_SELECTOR);
  if (main && 'MutationObserver' in window) {
    const observer = new MutationObserver(() => scheduleScan(document));
    observer.observe(main, { childList: true, subtree: true });
  }
})();

/* Cards de coleção: iniciar com a primeira imagem cadastrada no produto.
   A imagem da variante só assume o card após interação com um swatch. */
(() => {
  if (window.__mfCollectionFirstImageFix) return;
  window.__mfCollectionFirstImageFix = true;

  const imageCache = new Map();
  const CARD_SELECTOR = '[data-mf-card]';
  const SWATCH_SELECTOR = '.mf-swatch[data-variant-id]';

  const productPathFromCard = (card) => {
    let raw = card.getAttribute('data-product-url') || '';

    if (!raw) {
      const link = card.querySelector('[data-mf-card-link], a[href*="/products/"]');
      raw = link ? link.getAttribute('href') || link.href || '' : '';
    }

    if (!raw) return '';

    try {
      const url = new URL(raw, window.location.origin);
      if (!url.pathname.includes('/products/')) return '';
      return url.pathname.replace(/\/$/, '');
    } catch (error) {
      return '';
    }
  };

  const firstProductImage = (path) => {
    if (!path) return Promise.resolve('');
    if (imageCache.has(path)) return imageCache.get(path);

    const request = fetch(`${path}.js`, { credentials: 'same-origin' })
      .then((response) => {
        if (!response.ok) throw new Error('Não foi possível carregar o produto.');
        return response.json();
      })
      .then((product) => {
        let image = product?.images?.[0] || product?.featured_image || '';
        if (image && typeof image === 'object') image = image.src || image.url || '';
        return image || '';
      })
      .catch(() => '');

    imageCache.set(path, request);
    return request;
  };

  const shopifyImageUrl = (src, width) => {
    if (!src) return '';
    try {
      const url = new URL(src, window.location.origin);
      url.searchParams.set('width', String(width));
      return url.toString();
    } catch (error) {
      return src;
    }
  };

  const applyFirstImage = (card) => {
    if (!card || card.dataset.mfFirstImageReady === 'true') return;
    card.dataset.mfFirstImageReady = 'true';

    const image = card.querySelector('.mf-img--main');
    if (!image) return;

    const path = productPathFromCard(card);
    if (!path) return;

    firstProductImage(path).then((src) => {
      if (!src) return;
      if (card.dataset.mfUserSelectedImage === 'true') return;
      if (!document.documentElement.contains(card)) return;

      image.src = shopifyImageUrl(src, 900);
      image.srcset = [360, 540, 720, 900]
        .map((width) => `${shopifyImageUrl(src, width)} ${width}w`)
        .join(', ');
    });
  };

  const scanCards = (scope = document) => {
    if (scope.matches?.(CARD_SELECTOR)) applyFirstImage(scope);
    scope.querySelectorAll?.(CARD_SELECTOR).forEach(applyFirstImage);
  };

  document.addEventListener('click', (event) => {
    const swatch = event.target.closest?.(SWATCH_SELECTOR);
    if (!swatch) return;

    const card = swatch.closest(CARD_SELECTOR);
    if (card) card.dataset.mfUserSelectedImage = 'true';
  }, true);

  const start = () => {
    scanCards(document);

    if ('MutationObserver' in window && document.body) {
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === 1) scanCards(node);
          });
        });
      });

      observer.observe(document.body, { childList: true, subtree: true });
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();

/* Cards de coleção: % OFF calculado do preço "De" para o preço final no Pix. */
(() => {
  if (window.__mfPixDiscountBadgeReady) return;
  window.__mfPixDiscountBadgeReady = true;

  const CARD_SELECTOR = '[data-mf-card]';
  const PRICE_SELECTOR = '[data-mf-pix-main], [data-mf-compare]';

  const parseBRLToCents = (text) => {
    let value = String(text || '').replace(/[^0-9,.-]/g, '').trim();
    if (!value) return 0;

    if (value.includes(',')) {
      value = value.replace(/\./g, '').replace(',', '.');
    } else {
      value = value.replace(/,/g, '');
    }

    const number = Number(value);
    return Number.isFinite(number) ? Math.round(number * 100) : 0;
  };

  const centsToBRL = (cents) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(Number(cents || 0) / 100);
  };

  const updateCard = (card) => {
    if (!(card instanceof HTMLElement)) return;

    const compareEl = card.querySelector('[data-mf-compare]');
    const pixEl = card.querySelector('[data-mf-pix-main]');
    const badge = card.querySelector('.mf-badge');
    const amountBadge = card.querySelector('.mf-badge-amount');

    if (!compareEl || !pixEl || !badge) return;

    const compare = parseBRLToCents(compareEl.textContent);
    const pix = parseBRLToCents(pixEl.textContent);
    const hasDiscount = compare > pix && compare > 0 && pix > 0;

    if (!hasDiscount) {
      badge.style.display = 'none';
      if (amountBadge) amountBadge.style.display = 'none';
      return;
    }

    const difference = compare - pix;
    const percentage = Math.round((difference * 100) / compare);
    const badgeText = `${percentage}% OFF`;

    if (badge.textContent.trim() !== badgeText) {
      badge.textContent = badgeText;
    }
    badge.style.display = '';

    if (amountBadge) {
      const amountText = `Economize ${centsToBRL(difference)}`;
      if (amountBadge.textContent.trim() !== amountText) {
        amountBadge.textContent = amountText;
      }
      amountBadge.style.display = '';
    }
  };

  const scanCards = (scope = document) => {
    if (scope.matches?.(CARD_SELECTOR)) updateCard(scope);
    scope.querySelectorAll?.(CARD_SELECTOR).forEach(updateCard);
  };

  const start = () => {
    scanCards(document);

    document.addEventListener('click', (event) => {
      const swatch = event.target.closest?.('[data-mf-swatch]');
      if (!swatch) return;
      const card = swatch.closest(CARD_SELECTOR);
      if (card) window.setTimeout(() => updateCard(card), 0);
    }, true);

    const main = document.querySelector('#MainContent');
    if (main && 'MutationObserver' in window) {
      const observer = new MutationObserver((mutations) => {
        const cards = new Set();

        mutations.forEach((mutation) => {
          const target = mutation.target.nodeType === 1 ? mutation.target : mutation.target.parentElement;
          if (target && (target.matches?.(PRICE_SELECTOR) || target.closest?.(PRICE_SELECTOR))) {
            const card = target.closest?.(CARD_SELECTOR);
            if (card) cards.add(card);
          }

          mutation.addedNodes.forEach((node) => {
            if (node.nodeType !== 1) return;
            if (node.matches?.(CARD_SELECTOR)) cards.add(node);
            node.querySelectorAll?.(CARD_SELECTOR).forEach((card) => cards.add(card));
          });
        });

        cards.forEach(updateCard);
      });

      observer.observe(main, { childList: true, subtree: true, characterData: true });
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
